import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Mic, Zap, Wand2, Compass, MessageCircle, Bookmark, Share2,
  Image as ImageIcon, MapPin, Camera, Hash, Flame, Clock, ChevronDown,
  Lightbulb, Rocket, ArrowRight, PlayCircle,
} from "lucide-react";
import { TopNav } from "../components/TopNav";

/**
 * Guide — in-app user manual covering every feature of VYBE.
 * Designed to feel like a launch deck: hero → 4-step quickstart → feature deep-dives
 * → power-tips → final CTA.
 */
export default function Guide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      <main className="pt-28 pb-24 px-5 sm:px-8" data-testid="guide-page">
        {/* HERO */}
        <section className="mx-auto max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FFB45C] mb-3 inline-flex items-center gap-2">
            <Lightbulb className="h-3.5 w-3.5" /> User Guide
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
            Get the most out of <span className="text-gradient">VYBE</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl text-base sm:text-lg">
            Everything you can do — from one-tap demos to AI mood-boards and voice-prompted trips.
            Skim it once, you'll never miss a feature.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              data-testid="guide-cta-start"
              className="gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-5 py-3 inline-flex items-center gap-2 hover:opacity-95 active:scale-[0.99] transition"
            >
              <Rocket className="h-4 w-4" /> Start a trip
            </Link>
            <Link
              to="/discover"
              className="glass rounded-full px-5 py-3 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-[0.18em] text-zinc-200 hover:text-white border border-white/15 hover:border-white/30 transition"
            >
              <Compass className="h-4 w-4 text-[#00F2FE]" /> Browse Discover
            </Link>
          </div>
        </section>

        {/* QUICKSTART */}
        <section className="mx-auto max-w-5xl mt-20">
          <SectionHeading kicker="Quickstart" title="Generate your first viral trip in 30 seconds" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <StepCard
              num="01"
              icon={<Zap className="h-4 w-4 text-[#FFB45C]" />}
              title="Start with your own prompt"
              body="Type your vibe in the prompt bar and generate a live itinerary grounded in your exact brief."
            />
            <StepCard
              num="02"
              icon={<Wand2 className="h-4 w-4 text-[#FE0979]" />}
              title="Or write your own vibe"
              body="Type something like 'Cyberpunk Tokyo for IG reels'. Or fill the structured form on /create for finer control."
            />
            <StepCard
              num="03"
              icon={<Mic className="h-4 w-4 text-[#00F2FE]" />}
              title="Speak instead of typing"
              body="Tap the mic icon inside any prompt or chat input. Web Speech transcribes your words live — perfect on the go."
            />
            <StepCard
              num="04"
              icon={<Sparkles className="h-4 w-4 text-[#FFB45C]" />}
              title="Hit Generate"
              body="GPT-5.2 returns a 3-day creator brief with viral spots, captions, reel ideas, hashtags & a viral score."
            />
          </div>
        </section>

        {/* CORE FEATURES */}
        <section className="mx-auto max-w-5xl mt-24">
          <SectionHeading kicker="Every feature, explained" title="The full creator content kit" />

          <div className="mt-8 space-y-4">
            <FeatureRow
              icon={<Wand2 className="h-5 w-5 text-[#FE0979]" />}
              title="AI Itinerary Generator"
              tag="Core"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FE0979]">
                  <li>Type your vibe in one sentence, or use the structured form on <CodePill>/create</CodePill> (destination + travel vibe + content goal).</li>
                  <li>Returns a Creator Brief: 3 days × morning / afternoon / evening spots, reel idea, IG caption, why-it-performs, best time to shoot.</li>
                  <li>If our AI key is unavailable, a high-quality offline fallback still gives you a usable trip — demo never breaks.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Mic className="h-5 w-5 text-[#00F2FE]" />}
              title="Voice-to-Text Mic"
              tag="New"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#00F2FE]">
                  <li>Mic icon appears in: the landing prompt bar, the Create textarea, and the AI Copilot chat.</li>
                  <li>Tap once → grant browser permission → speak. Words append live to the input.</li>
                  <li>Tap again to stop. The mic pulses pink while listening.</li>
                  <li>Works on Chrome, Safari, Edge (mobile + desktop). Auto-hides on Firefox.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Flame className="h-5 w-5 text-[#FE0979]" />}
              title="Viral Score Engine"
              tag="Smart"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FE0979]">
                  <li>Every trip and spot gets a <strong>0–100 viral score</strong> with a tier label (Trending Hard / Riding the Wave / Slow Burn / Hidden Gem).</li>
                  <li>Hover the circular score to see the 5-factor breakdown: TikTok velocity (30%), aesthetic match (25%), reel-readiness (20%), accessibility (15%), uniqueness (10%).</li>
                  <li>Use it to decide which spot to feature first in your reel.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Camera className="h-5 w-5 text-[#FFB45C]" />}
              title="Creator Brief"
              tag="Reel-ready"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FFB45C]">
                  <li><strong>Day-by-day shoot plan</strong> with exact reel ideas, IG captions, why-it-performs notes.</li>
                  <li><strong>Top-5 viral spots</strong> ranked by score.</li>
                  <li>Interactive <strong>content checklist</strong> (tick off as you shoot).</li>
                  <li><strong>Suggested hashtags</strong> with one-click "Copy all".</li>
                  <li><strong>Final creator tip</strong> — the one thing most travellers miss.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<ImageIcon className="h-5 w-5 text-[#FFB45C]" />}
              title="AI Mood-Board (Nano Banana)"
              tag="Viral"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FFB45C]">
                  <li>On any itinerary page, tap <CodePill>Generate mood-board</CodePill>.</li>
                  <li>Gemini Nano Banana paints a cinematic <strong>3×3 aesthetic collage</strong> based on your top spots and vibe (~30–40s).</li>
                  <li>Tiles stream in live with a shimmer effect.</li>
                  <li>Tap <strong>Download PNG</strong> to grab the high-res image — perfect for IG story / Pinterest.</li>
                  <li>Hit <strong>Regenerate</strong> for a fresh batch of angles.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<MapPin className="h-5 w-5 text-[#00F2FE]" />}
              title="Spot Detail Pages"
              tag="On-the-ground"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#00F2FE]">
                  <li>Tap any spot card to open the full detail: viral score, best time, suggested caption, best photo angle, best reel idea.</li>
                  <li>One-tap <strong>Open in Google Maps</strong> deep-link.</li>
                  <li>TikTok inspiration panel — three hashtag-driven cards that open TikTok search for that exact spot.</li>
                  <li>"Nearby trending" chips to jump between spots.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Compass className="h-5 w-5 text-[#00F2FE]" />}
              title="Discover (Viral Travel DB)"
              tag="Browse"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#00F2FE]">
                  <li>20 destinations × 3 spots = <strong>60 hand-picked viral spots</strong> across Bali, Tokyo, Paris, Santorini, NYC and more.</li>
                  <li>Filter by trend status (Trending / Emerging / Hidden Gem / Oversaturated), city or aesthetic tag.</li>
                  <li>Click any destination → opens a Spots Drawer with 3 spots and a <CodePill>Build my AI trip from this</CodePill> shortcut.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<MessageCircle className="h-5 w-5 text-[#FE0979]" />}
              title="Ask VYBE (AI Copilot)"
              tag="Conversational"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FE0979]">
                  <li>Floating chat bubble bottom-right of any itinerary page.</li>
                  <li>Knows your full trip context: spots, days, vibe, hashtags, viral score.</li>
                  <li>Quick prompts: "Make this more budget-friendly", "More hidden gems", "Best sunset spot?", "One-day version", and more.</li>
                  <li>Voice input works here too — tap the mic.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Bookmark className="h-5 w-5 text-[#00F2FE]" />}
              title="Save Trip (works for guests)"
              tag="No login needed"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#00F2FE]">
                  <li>Hit <strong>Save trip</strong> on any itinerary. Saved instantly to your browser (no account required).</li>
                  <li>Sign in with Google to sync trips across devices and never lose them.</li>
                  <li>Open <CodePill>/dashboard</CodePill> ("Saved" in the nav) to revisit, share, or remove saved trips.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Share2 className="h-5 w-5 text-[#FE0979]" />}
              title="Share Card + Public Link"
              tag="Make it viral"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FE0979]">
                  <li><strong>Share button</strong> → public URL with rich Open Graph preview (unfurls beautifully in WhatsApp / iMessage / X / Discord / Slack / IG DM).</li>
                  <li><strong>Share Card button</strong> → downloads a 9:16 IG-story-style PNG with cover + top 3 spots + viral score + your vibe tags.</li>
                  <li>On mobile, the native "share to Instagram / TikTok" sheet pops up automatically.</li>
                </ul>
              }
            />

            <FeatureRow
              icon={<Hash className="h-5 w-5 text-[#FFB45C]" />}
              title="Hashtag Pack & Captions"
              tag="Copy in one tap"
              children={
                <ul className="space-y-1.5 list-disc pl-5 marker:text-[#FFB45C]">
                  <li>Every itinerary ships with 8–12 curated hashtags ready for Reels / TikTok.</li>
                  <li>Click <strong>Copy all</strong> in the Creator Brief section.</li>
                  <li>Each spot also has its own caption_idea you can copy individually.</li>
                </ul>
              }
            />
          </div>
        </section>

        {/* POWER TIPS */}
        <section className="mx-auto max-w-4xl mt-24">
          <SectionHeading kicker="Power tips" title="Tricks the regulars use" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <TipCard
              title="Stack your prompt"
              body="The more aesthetic + format keywords you stack ('cinematic', 'old-money', 'cliff-side', 'reel-ready'), the more on-vibe the output."
            />
            <TipCard
              title="Regenerate the mood-board"
              body="Each regenerate gives you a brand new angle — re-roll a few times to get one that screenshots perfectly."
            />
            <TipCard
              title="Use Discover as a launchpad"
              body="Open a destination on Discover, click a spot you like, then tap 'Build my AI trip from this' for a focused itinerary."
            />
            <TipCard
              title="Voice prompt while travelling"
              body="On the road and inspired? Open the Copilot, tap the mic, and ask 'Add a hidden coffee spot near here' — it knows your trip."
            />
            <TipCard
              title="Sign in to never lose trips"
              body="Saves work for guests too, but signing in with Google syncs everything across phone, laptop and tablet."
            />
            <TipCard
              title="Share before the trip, not after"
              body="Sharing the public link or PNG before you travel often gets more engagement than post-trip recaps. The viral score is your hook."
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl mt-24">
          <SectionHeading kicker="FAQ" title="Quick answers" />
          <div className="mt-6 space-y-2">
            <FaqItem
              q="Do I need an account?"
              a="No. You can generate, view, save and share trips entirely as a guest. Signing in with Google only adds cross-device sync."
            />
            <FaqItem
              q="Is it free?"
              a="Yes — generation, mood-boards, sharing, all free during early access."
            />
            <FaqItem
              q="Why doesn't the mic appear in my browser?"
              a="Web Speech needs Chrome / Safari / Edge. Firefox and very old browsers don't support it — typing still works perfectly."
            />
            <FaqItem
              q="Are mood-board images real photos?"
              a="No — they're AI-generated by Gemini Nano Banana, designed to capture the vibe of each spot. Use them for inspiration boards, not as real proof of location."
            />
            <FaqItem
              q="How accurate are the viral scores?"
              a="They blend TikTok hashtag velocity, aesthetic match, reel-readiness, accessibility and uniqueness into a single 0–100 score. Hover the score to see the breakdown."
            />
            <FaqItem
              q="Can I edit a generated itinerary?"
              a="Open the AI Copilot on any trip and just ask — 'make it more romantic', 'one-day version', 'more food spots'. It updates in chat with full context."
            />
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mx-auto max-w-3xl mt-24 text-center">
          <div className="glass-strong rounded-3xl p-8 sm:p-10 ring-neon">
            <PlayCircle className="h-8 w-8 mx-auto text-[#FFB45C] mb-3" />
            <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
              Ready to plan your first viral trip?
            </h3>
            <p className="text-zinc-400 mt-2 text-sm sm:text-base">
              30 seconds. No account needed. We'll handle the spots, captions and mood-board.
            </p>
            <button
              onClick={() => navigate("/")}
              data-testid="guide-cta-bottom"
              className="mt-6 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-6 py-3.5 inline-flex items-center gap-2 hover:opacity-95 active:scale-[0.98] transition"
            >
              <Sparkles className="h-4 w-4" /> Start now <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

const SectionHeading = ({ kicker, title }) => (
  <div>
    <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2FE] mb-2">{kicker}</div>
    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{title}</h2>
  </div>
);

const StepCard = ({ num, icon, title, body }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
    className="glass rounded-2xl p-5 hover-lift"
  >
    <div className="text-[10px] font-display font-black tracking-[0.3em] text-zinc-500">{num}</div>
    <div className="mt-2 inline-flex items-center gap-2">
      {icon}
      <h3 className="font-display font-bold text-base sm:text-lg leading-tight">{title}</h3>
    </div>
    <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{body}</p>
  </motion.div>
);

const FeatureRow = ({ icon, title, tag, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.45 }}
    className="glass-strong rounded-2xl p-5 sm:p-6"
  >
    <div className="flex items-start gap-4">
      <div className="shrink-0 h-10 w-10 rounded-xl bg-white/5 border border-white/10 inline-flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight">{title}</h3>
          {tag && (
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#FFB45C] bg-[#FFB45C]/10 border border-[#FFB45C]/20 rounded-full px-2 py-0.5">
              {tag}
            </span>
          )}
        </div>
        <div className="text-sm text-zinc-300 mt-3 leading-relaxed">{children}</div>
      </div>
    </div>
  </motion.div>
);

const TipCard = ({ title, body }) => (
  <div className="glass rounded-2xl p-5">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FFB45C] mb-1.5 inline-flex items-center gap-1.5">
      <Lightbulb className="h-3 w-3" /> Pro tip
    </div>
    <h4 className="font-display font-bold text-base">{title}</h4>
    <p className="text-zinc-400 text-sm mt-1.5 leading-relaxed">{body}</p>
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-2xl">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`faq-${q.toLowerCase().slice(0, 12).replace(/\s+/g, "-")}`}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
      >
        <span className="font-display font-bold text-sm sm:text-base">{q}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-400 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-zinc-300 leading-relaxed -mt-1">{a}</div>
      )}
    </div>
  );
};

const CodePill = ({ children }) => (
  <code className="bg-white/8 border border-white/10 rounded-md px-1.5 py-0.5 text-[12px] font-mono text-[#FFB45C]">
    {children}
  </code>
);
